// ============================================
// StocktakePage
// Kiểm kê định kỳ (sheet 2 — mục #30)
// ============================================
//
// Nguồn: /inventory/stocktakes/ (mở phiên, lưu số đếm, chốt, huỷ).
//
// PHÂN VAI đã chốt — đọc trước khi định gộp hai đường này làm một:
//   - `SET_ABSOLUTE` (AdjustStockModal ở trang Tồn kho) = sửa lẻ MỘT mặt hàng
//     ngoài phiên. Nhanh, không hồ sơ.
//   - Phiên kiểm kê (trang này) = đếm định kỳ NHIỀU mặt hàng, có mã phiên, có
//     lúc chốt, có bản ghi từng dòng.
// Cả hai cuối cùng đều đổ về /inventory/adjustments/ nên lịch sử vẫn là một.
//
// BA điểm của hợp đồng BE chi phối toàn bộ giao diện dưới đây:
//   1. Mỗi cửa hàng chỉ MỘT phiên OPEN (ràng buộc DB). Mở phiên thứ hai trả 400
//      chứ không trả lại phiên đang mở — nên trang phải hỏi phiên OPEN trước và
//      mở thẳng phiên đó, không bao giờ hiện nút "Mở phiên" khi đã có.
//   2. `expected_quantity` là snapshot TẠI LÚC LƯU số đếm, không phải lúc mở
//      phiên. Vì thế mỗi lần lưu xong phải đọc lại phiên; đừng tự tính variance
//      ở client từ con số expected cũ.
//   3. Chốt phiên IDEMPOTENT theo kiểu "chỉ ăn một lần": bấm lần hai trả 400.
//      Phiên FINALIZED/CANCELLED đóng băng, mọi sửa đổi đều 400.
//
// Endpoint chưa lên QA nên hook đặt `retry: false` — 404 vào thẳng error state.

import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { Stocktake, StocktakeLine, Store, UUID } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import {
  PageContainer,
  PageHeader,
  ActionButton,
  SelectDropdown,
  DataTable,
  type Column,
} from '../../components/shared';
import {
  useCancelStocktake,
  useCreateStocktake,
  useFinalizeStocktake,
  useSaveStocktakeLine,
  useStocktake,
  useStocktakes,
  useStores,
} from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import { extractList } from '../../utils/apiList';
import { ProductPicker, type PickedProduct } from './components';

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_STYLES: Record<string, string> = {
  OPEN: 'bg-emerald-50 text-emerald-700',
  FINALIZED: 'bg-blue-50 text-blue-700',
  CANCELLED: 'bg-slate-100 text-slate-600',
};

function StatusPill({ status, label }: { status: string; label: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
        STATUS_STYLES[status] ?? 'bg-slate-100 text-slate-700'
      }`}
    >
      {label}
    </span>
  );
}

export function StocktakePage() {
  const { t } = useI18n();
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);

  const [storeId, setStoreId] = useState<string>(selectedStoreId ?? '');
  /** Phiên CŨ đang xem ở chế độ đóng băng; `null` = đang ở màn phiên hiện hành. */
  const [viewingId, setViewingId] = useState<string | null>(null);

  const { data: storesData } = useStores({ page: 1, page_size: 100, business_status: 'ACTIVE' });
  const stores = useMemo(() => extractList<Store>(storesData), [storesData]);

  const storeOptions = useMemo(
    () => stores.map((store) => ({ value: store.id, label: `${store.code} - ${store.name}` })),
    [stores]
  );

  // Hỏi phiên OPEN TRƯỚC mọi thứ khác: có phiên thì mở thẳng nó, vì mở phiên
  // thứ hai cho cùng cửa hàng là 400 ở tầng database.
  const {
    data: openData,
    isLoading: isLoadingOpen,
    error: openError,
  } = useStocktakes(
    { store_id: (storeId as UUID) || undefined, status: 'OPEN' },
    { enabled: !!storeId }
  );

  const openSession = useMemo(() => extractList<Stocktake>(openData)[0] ?? null, [openData]);

  const { data: historyData, isLoading: isLoadingHistory } = useStocktakes(
    { store_id: (storeId as UUID) || undefined, page: 1, page_size: 20 },
    { enabled: !!storeId }
  );

  const history = useMemo(() => extractList<Stocktake>(historyData), [historyData]);

  const activeId = viewingId ?? openSession?.id ?? undefined;
  const { data: sessionDetail, isLoading: isLoadingDetail } = useStocktake(activeId, {
    enabled: !!activeId,
  });

  const isFrozen = viewingId !== null && sessionDetail?.status !== 'OPEN';

  return (
    <PageContainer>
      <PageHeader
        title={t('stocktakeTitle')}
        subtitle={t('stocktakeSubtitle')}
        actions={
          viewingId ? (
            <ActionButton variant="secondary" onClick={() => setViewingId(null)}>
              {t('stocktakeBackToCurrent')}
            </ActionButton>
          ) : undefined
        }
      />

      {openError && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {openError instanceof Error ? openError.message : t('stocktakeLoadError')}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
          {t('storeColumn')}
        </label>
        <SelectDropdown
          options={storeOptions}
          value={storeId || null}
          onChange={(val) => {
            setStoreId(val);
            setViewingId(null);
          }}
          placeholder={t('stocktakeSelectStore')}
          className="w-full sm:w-[280px]"
        />
        {/* Phiên luôn thuộc ĐÚNG một cửa hàng — không có chế độ "tất cả cửa hàng". */}
        <p className="mt-1.5 text-xs text-slate-500">{t('stocktakeStoreHint')}</p>
      </div>

      {!storeId && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600">
          {t('stocktakeNoStore')}
        </div>
      )}

      {storeId && isLoadingOpen && (
        <div className="mt-4 h-24 animate-pulse rounded-xl bg-slate-100" />
      )}

      {/* Phiên đang xem: phiên OPEN của cửa hàng, hoặc một phiên cũ đã bấm vào. */}
      {storeId && !isLoadingOpen && activeId && (
        <SessionPanel
          session={sessionDetail}
          isLoading={isLoadingDetail}
          isFrozen={isFrozen}
          storeId={storeId}
          onClosed={() => setViewingId(null)}
        />
      )}

      {/* Không có phiên OPEN và không xem phiên cũ → cho mở phiên mới. */}
      {storeId && !isLoadingOpen && !activeId && <OpenSessionCard storeId={storeId} />}

      {/* Danh sách phiên cũ — chỉ ĐỌC. */}
      {storeId && !viewingId && (
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-base font-bold text-slate-900">{t('stocktakeHistoryTitle')}</h3>
          <p className="mt-0.5 text-xs text-slate-500">{t('stocktakeHistorySubtitle')}</p>

          {isLoadingHistory && <div className="mt-3 h-16 animate-pulse rounded-lg bg-slate-100" />}

          {!isLoadingHistory && history.length === 0 && (
            <p className="mt-4 text-sm text-slate-500">{t('stocktakeHistoryEmpty')}</p>
          )}

          {!isLoadingHistory && history.length > 0 && (
            <ul className="mt-3 space-y-2">
              {history.map((session) => (
                <li key={session.id}>
                  <button
                    type="button"
                    onClick={() => setViewingId(session.id)}
                    className="flex w-full flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{session.code}</p>
                      <p className="text-xs text-slate-500">{formatDateTime(session.created_at)}</p>
                    </div>
                    <StatusPill
                      status={session.status}
                      label={t(`stocktakeStatus${session.status}`)}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </PageContainer>
  );
}

// ============================================
// Mở phiên mới
// ============================================

function OpenSessionCard({ storeId }: { storeId: string }) {
  const { t } = useI18n();
  const { mutate: createStocktake, isPending } = useCreateStocktake();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h3 className="text-base font-bold text-slate-900">{t('stocktakeOpenTitle')}</h3>
      <p className="mt-1 text-sm text-slate-600">{t('stocktakeOpenDesc')}</p>

      <div className="mt-4 max-w-lg">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
          {t('stocktakeNote')}
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t('stocktakeNoteHint')}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
        />
      </div>

      {error && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <button
        type="button"
        disabled={isPending}
        onClick={() => {
          setError(null);
          createStocktake(
            { store_id: storeId, note: note.trim() || undefined },
            {
              onSuccess: () => toast.success(t('stocktakeOpenSuccess')),
              onError: (err: unknown) => {
                // 400 hay gặp nhất ở đây là "cửa hàng đã có phiên OPEN" — nói rõ
                // để người dùng bấm tải lại thay vì thử mở tiếp.
                setError(err instanceof Error ? err.message : t('stocktakeOpenError'));
              },
            }
          );
        }}
        className="mt-4 rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
      >
        {isPending ? t('saving') : t('stocktakeOpenButton')}
      </button>
    </div>
  );
}

// ============================================
// Phiên đang xem (đang mở hoặc đã đóng băng)
// ============================================

interface SessionPanelProps {
  session?: Stocktake;
  isLoading: boolean;
  isFrozen: boolean;
  storeId: string;
  onClosed: () => void;
}

function SessionPanel({ session, isLoading, isFrozen, storeId, onClosed }: SessionPanelProps) {
  const { t } = useI18n();
  const { mutate: saveLine, isPending: isSaving } = useSaveStocktakeLine();
  const { mutate: finalize, isPending: isFinalizing } = useFinalizeStocktake();
  const { mutate: cancelSession, isPending: isCancelling } = useCancelStocktake();

  const [product, setProduct] = useState<PickedProduct | null>(null);
  const [counted, setCounted] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'finalize' | 'cancel' | null>(null);

  const lines = useMemo(() => session?.lines ?? [], [session]);

  // `totals` của BE là nguồn chính; tự đếm chỉ để không hiện "—" khi endpoint
  // trả thiếu totals.
  const lineCount = session?.totals?.lines ?? lines.length;
  const varianceCount =
    session?.totals?.with_variance ?? lines.filter((line) => line.variance !== 0).length;

  const columns: Column<StocktakeLine>[] = [
    {
      key: 'product',
      header: t('product'),
      render: (row) => (
        <div>
          <p className="text-sm font-semibold text-slate-900">{row.product_name ?? row.product_id}</p>
          <p className="text-xs text-slate-500">{row.product_sku ?? row.product_id}</p>
        </div>
      ),
    },
    {
      key: 'expected_quantity',
      header: t('stocktakeExpected'),
      width: '130px',
      render: (row) => <p className="text-sm text-slate-700">{row.expected_quantity}</p>,
    },
    {
      key: 'counted_quantity',
      header: t('stocktakeCounted'),
      width: '130px',
      render: (row) => (
        <p className="text-sm font-semibold text-slate-900">{row.counted_quantity}</p>
      ),
    },
    {
      key: 'variance',
      header: t('stocktakeVariance'),
      width: '120px',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
            row.variance === 0
              ? 'bg-slate-100 text-slate-700'
              : row.variance > 0
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-red-50 text-red-700'
          }`}
        >
          {row.variance > 0 ? `+${row.variance}` : row.variance}
        </span>
      ),
    },
  ];

  if (isLoading || !session) {
    return <div className="mt-4 h-40 animate-pulse rounded-xl bg-slate-100" />;
  }

  const handleSaveLine = () => {
    setError(null);
    if (!product) {
      setError(t('stocktakeErrorProduct'));
      return;
    }
    const parsed = Number.parseInt(counted, 10);
    // 0 là số đếm HỢP LỆ ("đếm ra không còn cái nào"), nên chỉ chặn số âm và ô trống.
    if (!Number.isFinite(parsed) || parsed < 0) {
      setError(t('stocktakeErrorCounted'));
      return;
    }

    saveLine(
      { stocktakeId: session.id, productId: product.id, countedQuantity: parsed },
      {
        onSuccess: () => {
          toast.success(t('stocktakeLineSaved'));
          setProduct(null);
          setCounted('');
        },
        onError: (err: unknown) => {
          setError(err instanceof Error ? err.message : t('stocktakeErrorGeneric'));
        },
      }
    );
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Đầu phiên */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-base font-bold text-slate-900">{session.code}</p>
            <StatusPill status={session.status} label={t(`stocktakeStatus${session.status}`)} />
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            {formatDateTime(session.created_at)}
            {session.note ? ` · ${session.note}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-slate-600">
            {t('stocktakeLinesCount')}: <strong className="text-slate-900">{lineCount}</strong>
          </span>
          <span className="text-slate-600">
            {t('stocktakeVarianceCount')}:{' '}
            <strong className={varianceCount > 0 ? 'text-orange-600' : 'text-slate-900'}>
              {varianceCount}
            </strong>
          </span>
        </div>
      </div>

      {isFrozen && (
        <div className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">
          {t('stocktakeFrozenHint')}
        </div>
      )}

      {/* Ô đếm — chỉ có ở phiên OPEN */}
      {!isFrozen && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-base font-bold text-slate-900">{t('stocktakeCountTitle')}</h3>
          {/* Nói rõ ý nghĩa snapshot: đếm lúc cửa hàng vẫn bán thì variance là
              "mắt thấy trừ đi thứ hệ thống tin NGAY LÚC LƯU", không phải lúc mở phiên. */}
          <p className="mt-0.5 text-xs text-slate-500">{t('stocktakeSnapshotHint')}</p>

          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_160px_auto] sm:items-end">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('product')}
              </label>
              <ProductPicker
                selected={product}
                onSelect={setProduct}
                storeId={storeId}
                placeholder={t('stocktakeProductHint')}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('stocktakeCounted')}
              </label>
              <input
                type="number"
                min={0}
                value={counted}
                onChange={(e) => setCounted(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="button"
              onClick={handleSaveLine}
              disabled={isSaving}
              className="h-10 rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? t('saving') : t('stocktakeSaveLine')}
            </button>
          </div>

          {error && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>
      )}

      {/* Các dòng đã đếm */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={lines}
          rowKey="product_id"
          showFilter={false}
          showHeaderMenu={false}
          emptyMessage={t('stocktakeNoLines')}
          className="border-0 shadow-none"
        />
      </div>

      {!isFrozen && (
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmAction('cancel')}
            disabled={isCancelling}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            {t('stocktakeCancelButton')}
          </button>
          <button
            type="button"
            onClick={() => setConfirmAction('finalize')}
            disabled={isFinalizing}
            className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {t('stocktakeFinalizeButton')}
          </button>
        </div>
      )}

      {confirmAction && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setConfirmAction(null)}
        >
          <div
            className="w-full max-w-[460px] rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-slate-900">
              {confirmAction === 'finalize' ? t('stocktakeFinalizeTitle') : t('stocktakeCancelTitle')}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {confirmAction === 'finalize'
                ? // Nêu THẲNG số bút toán sắp sinh: chốt phiên là ghi vào sổ,
                  // không hoàn tác được, và người bấm phải biết quy mô.
                  t('stocktakeFinalizeConfirm')
                    .replace('{count}', String(varianceCount))
                    .replace('{code}', session.code)
                : t('stocktakeCancelConfirm').replace('{code}', session.code)}
            </p>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                disabled={isFinalizing || isCancelling}
                onClick={() => {
                  if (confirmAction === 'finalize') {
                    finalize(session.id, {
                      onSuccess: (res) => {
                        toast.success(
                          t('stocktakeFinalizeSuccess')
                            .replace('{applied}', String(res.adjustments_applied ?? 0))
                            .replace('{unchanged}', String(res.lines_unchanged ?? 0))
                        );
                        setConfirmAction(null);
                        onClosed();
                      },
                      onError: (err: unknown) => {
                        // Bấm lần hai trả 400 dù lần đầu ĐÃ ghi xong — báo lỗi
                        // kèm gợi ý mở lại phiên để xem trạng thái thật, đừng
                        // để người dùng bấm thêm lần nữa.
                        toast.error(err instanceof Error ? err.message : t('stocktakeFinalizeError'));
                        setConfirmAction(null);
                      },
                    });
                  } else {
                    cancelSession(session.id, {
                      onSuccess: () => {
                        toast.success(t('stocktakeCancelSuccess'));
                        setConfirmAction(null);
                        onClosed();
                      },
                      onError: (err: unknown) => {
                        toast.error(err instanceof Error ? err.message : t('stocktakeCancelError'));
                        setConfirmAction(null);
                      },
                    });
                  }
                }}
                className={`rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50 ${
                  confirmAction === 'finalize'
                    ? 'bg-primary-600 hover:bg-primary-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {confirmAction === 'finalize' ? t('stocktakeFinalizeButton') : t('stocktakeCancelButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
